import * as React from "react";
import { useMemo, useState } from "react";
import { EAIClient } from "Services/EAIClient";
import { SavedViewListItem, revalidateSavedViews } from "@/hooks/saved-views";
import { useToast } from "@/hooks/useToast";
import {
    RemoveSavedViewModal,
    RenameSavedViewModal,
    ShareSavedViewModal,
    errorMessage,
} from "./savedViewModals";

interface UseSavedViewManagementOptions {
    /**
     * Captures the current live Tableau URL. Provided only where a live
     * dashboard is present (the in-dashboard panel); enables "Update with
     * Current State".
     */
    getCurrentUrl?: () => Promise<string | undefined>;
}

/**
 * Encapsulates the shared saved-view management actions (rename, update with
 * current state, share, remove) and the modals they drive, so the in-dashboard
 * and home-page panels behave identically. All mutations revalidate every
 * cached saved-views list, so both panels stay in sync without a reload.
 */
export function useSavedViewManagement({ getCurrentUrl }: UseSavedViewManagementOptions = {}) {
    const [renameTarget, setRenameTarget] = useState<SavedViewListItem | null>(null);
    const [removeTarget, setRemoveTarget] = useState<SavedViewListItem | null>(null);
    const [shareTarget, setShareTarget] = useState<SavedViewListItem | null>(null);
    const [updatingId, setUpdatingId] = useState<string | null>(null);

    const client = useMemo(() => new EAIClient(), []);
    const { toast } = useToast();

    const canUpdate = typeof getCurrentUrl === "function";

    const handleRenameConfirm = async (newName: string) => {
        if (!renameTarget) return;
        await client.updateSavedView(renameTarget.ruuid, { name: newName });
        await revalidateSavedViews();
        toast({ type: "success", title: "View renamed", message: `Renamed to "${newName}".` });
    };

    const updateWithCurrentState = async (view: SavedViewListItem) => {
        if (!getCurrentUrl) return;
        setUpdatingId(view.ruuid);
        try {
            const url = await getCurrentUrl();
            if (!url) {
                toast({ type: "error", title: "Update failed", message: "Could not capture the current dashboard view." });
                return;
            }
            await client.updateSavedView(view.ruuid, { url });
            await revalidateSavedViews();
            toast({ type: "success", title: "View updated", message: `"${view.name}" now points at the current view.` });
        } catch (e: any) {
            toast({ type: "error", title: "Update failed", message: errorMessage(e) });
        } finally {
            setUpdatingId(null);
        }
    };

    const handleRemoveConfirm = async () => {
        if (!removeTarget) return;
        try {
            await client.leaveSavedView(removeTarget.ruuid);
        } catch (e: any) {
            // The view no longer exists — treat as success per spec.
            if (e?.response?.status !== 404) throw e;
        }
        await revalidateSavedViews();
        toast({ type: "success", title: "View removed", message: `"${removeTarget.name}" was removed from your list.` });
    };

    const hasModalOpen = renameTarget !== null || removeTarget !== null || shareTarget !== null;

    const modals = (
        <>
            <RenameSavedViewModal view={renameTarget} onConfirm={handleRenameConfirm} onClose={() => setRenameTarget(null)} />
            <RemoveSavedViewModal view={removeTarget} onConfirm={handleRemoveConfirm} onClose={() => setRemoveTarget(null)} />
            {shareTarget && <ShareSavedViewModal view={shareTarget} onClose={() => setShareTarget(null)} />}
        </>
    );

    return {
        // openers
        openRename: setRenameTarget,
        openRemove: setRemoveTarget,
        openShare: setShareTarget,
        updateWithCurrentState,
        // state
        updatingId,
        hasModalOpen,
        canUpdate,
        // rendering
        modals,
    };
}
